import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { createPopper } from '@popperjs/core';
import { useRecoilState, useRecoilValue } from 'recoil';
import { CQuizProcess, TQuizProcess, EQuizProcessName } from 'modules/learning/quizTypes';
import {
  isPageOpenedState,
  currentProcessState,
  isGradedState,
  descriptionKeyState,
  quizDetailPageWidthState,
} from 'modules/learning/recoilState';
import { Modal, Button, Row } from 'antd';
import styled from 'styled-components';
import { Colors, device, size } from 'GlobalStyles';

const IntroduceProcessModal = () => {
  const boundary: any = document.querySelector('#popper_boundary');
  const [referenceElement, setReferenceElement] = useState<HTMLElement>();
  const [popperElement, setPopperElement] = useState();
  const [arrowElement, setArrowElement] = useState();
  const [isPageOpened, setIsPageOpened] = useRecoilState<boolean>(isPageOpenedState);
  const [currentProcess, setcCurrentProcess] = useRecoilState(currentProcessState);
  const isGraded = useRecoilValue(isGradedState);
  const descriptionKey = useRecoilValue(descriptionKeyState);
  const widthSize = useRecoilValue(quizDetailPageWidthState);
  const [targetReference, setTargetReference] = useState<HTMLElement>();

  //popper의 위치 설정
  const locatePlacement = (current: string) => {
    if (widthSize > size.sm) {
      if (current === 'question' || current === 'commentary') return 'right';
      if (current === 'option' || current === 'answer') return 'left';
      if (current === 'grading_button') return 'bottom';
      if (current === 'quiz_step') return 'bottom';
    } else {
      if (current === 'question' || current === 'commentary') return 'bottom';
      if (current === 'option' || current === 'answer') return 'bottom';
      if (current === 'grading_button') return 'bottom';
      if (current === 'quiz_step') return 'bottom';
    }
  };

  //reference 요소와 popper간의 간격 설정
  const settingOffset = (current: string): any => {
    let offset;
    //문제 페이지 column 정렬되는 width 이전
    if (widthSize > size.sm) {
      if (current === 'grading_button') {
        offset = [-70, -300];
      } else if (current === 'quiz_step') {
        offset = [-100, 15];
      } else {
        offset = [-170, 15];
      }
    } else {
      //문제 페이지 column 정렬되는 width 이후
      if (current === 'grading_button') {
        offset = [-200, -270];
      } else if (current === 'quiz_step') {
        offset = [-130, -2];
      } else if (current === 'option' || current === 'answer') {
        offset = [-150, -340];
      } else {
        offset = [-150, -720];
      }
    }

    return offset;
  };

  //Popper의 arrow 위치 조정
  const transformArrow = (current: string) => {
    if (widthSize > size.sm) {
      if (current === 'question' || current === 'commentary') return 'translate(-27px, 70px)';
      if (current === 'option' || current === 'answer') return 'translate(232px, 80px)';
      if (current === 'grading_button') return 'translate(130px, 61px)';
      if (current === 'quiz_step') return 'translate(130px, -105px)';
    } else {
      if (current === 'question' || current === 'commentary') return 'translate(40px, -83px)';
      if (current === 'option') return 'translate(40px, -105px)';
      if (current === 'answer') return 'translate(40px, -83px)';
      if (current === 'grading_button') return 'translate(200px, 61px)';
      if (current === 'quiz_step') return 'translate(130px, -105px)';
    }
  };

  //라이브러리 사용하여 popper 생성
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: locatePlacement(currentProcess.name),
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: boundary,
        },
      },
      {
        name: 'offset',
        options: {
          offset: useMemo(() => settingOffset(currentProcess.name), [currentProcess]),
        },
      },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  });

  const onClickNextProcess = (currentStepNum: number) => {
    if (CQuizProcess.length > currentStepNum) {
      const nextProcess = CQuizProcess.find((el: TQuizProcess) => el.step === currentStepNum + 1);
      nextProcess &&
        setcCurrentProcess({
          ...currentProcess,
          step: nextProcess?.step,
          name: nextProcess?.name,
        });
    } else {
      handleCancel();
    }
  };

  const onClickPrevProcess = (currentStepNum: number) => {
    const prevProcess = CQuizProcess.find((el: TQuizProcess) => el.step === currentStepNum - 1);
    prevProcess &&
      setcCurrentProcess({
        ...currentProcess,
        step: prevProcess?.step,
        name: prevProcess?.name,
      });
  };

  function handleCancel() {
    setIsPageOpened(false);
    setcCurrentProcess({ ...currentProcess, step: 0, name: '' });
  }

  useEffect(() => {
    setTargetReference(
      document.querySelector(
        //채점된 상태이고 탭이 해설에 가있을 때
        isGraded && descriptionKey === 'commentary'
          ? `#${currentProcess.name === 'question' ? 'commentary' : currentProcess.name}_popper_reference`
          : //채점한 상태가 아니거나 문제 탭에 가있을 때
            `#${currentProcess.name === 'commentary' ? 'question' : currentProcess.name}_popper_reference`,
      ) as HTMLElement,
    );
  }, [currentProcess, descriptionKey]); //문제, 해설 popper가 뜨고 있는 경우에 탭변동을 할 경우에도 reference element에 반영되어야 모달 위치가 이상해지지 않음

  useEffect(() => {
    if (targetReference?.id === `${EQuizProcessName.GradingButton}_popper_reference`) {
      //채점하기 버튼일 때는 element 시작점이 아니라 끝점을 기준으로
      targetReference?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (
      //문제/해설의 경우에는 스크롤 top에 고정되어 있도록
      targetReference?.id === `${EQuizProcessName.Question}_popper_reference` ||
      targetReference?.id === `${EQuizProcessName.Commentary}_popper_reference`
    ) {
      window.scrollTo(0, 200);
      // targetReference?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      targetReference?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [targetReference]);

  return (
    <SteyledProcessModalWrapper>
      {(document.querySelector(`#popper_boundary`) as any) &&
        ReactDOM.createPortal(
          <div></div>,
          //문재, 해설 똑같은 걸로 wrapper되어 있기 때문에 (StyleDescriptionHtmlWrapper)
          document.querySelector(
            // `#${currentProcess.name}_popper_reference`
            `#popper_boundary`,
          ) as any,
        )}
      {targetReference &&
        ReactDOM.createPortal(
          <div
            ref={setReferenceElement as any}
            style={{
              ...styles.reference,
              position: 'absolute',
              top: 0,
              left: 0,
              width: (targetReference as HTMLElement)?.offsetWidth,
              height: (targetReference as HTMLElement)?.offsetHeight,
            }}
            {...attributes.reference}
          ></div>,
          //문재, 해설 똑같은 걸로 wrapper되어 있기 때문에 (StyleDescriptionHtmlWrapper)
          //문제를 풀었고(isGraded===true) && 탭이 해설에 있을 때 commentary_popper_reference
          //탭이 문제이 있을 때 question_popper_reference
          targetReference as HTMLElement,
        )}

      <div
        id='tooltip'
        ref={setPopperElement as any}
        style={{
          display: 'inline-block',
        }}
        {...attributes.popper}
      >
        <Modal
          visible={true}
          onCancel={handleCancel}
          getContainer={false}
          width={widthSize > size.sm ? 260 : 280}
          mask={false}
          maskClosable={false}
          style={{
            ...styles.popper,
          }}
          title={
            <div>
              <span
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bolder',
                }}
              >
                {CQuizProcess.find((el: TQuizProcess) => el.step === currentProcess.step)?.title}
              </span>
            </div>
          }
          footer={
            <Row justify='space-between'>
              <div style={{ color: `${Colors.grey2}` }}>
                {currentProcess.step}/{CQuizProcess.length}
              </div>
              <div>
                <Button onClick={() => onClickPrevProcess(currentProcess.step)}>이전</Button>
                <Button className='ant-btn-primary' onClick={() => onClickNextProcess(currentProcess.step)}>
                  다음
                </Button>
              </div>
            </Row>
          }
        >
          <div>{CQuizProcess.find((el: TQuizProcess) => el.step === currentProcess.step)?.description}</div>
          <div
            id='arrow'
            ref={setArrowElement as any}
            style={{
              ...styles.arrow,
              transform: transformArrow(currentProcess.name),
              backgroundColor: 'white',
            }}
          ></div>
        </Modal>
      </div>
    </SteyledProcessModalWrapper>
  );
};

export default IntroduceProcessModal;

const SteyledProcessModalWrapper = styled.div`
  & .ant-modal-footer .ant-btn > span {
    height: unset;
  }
  & .ant-modal-footer .ant-btn {
    @media screen and ${device.md} {
      margin: 0px 5px;
    }
  }
  & .ant-modal-header {
    padding: 16px 24px 0px 24px;
  }
  & .ant-modal-wrap {
    overflow: unset;
    /* transform: rotate(180deg); */
  }
  & #arrow,
  & #arrow::before {
    position: absolute;
    width: 8px;
    height: 8px;
    background: inherit;
  }

  & #arrow {
    visibility: hidden;
  }

  & #arrow::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
  }
`;
